import { useNavigate } from "@tanstack/react-router";
import { Skeleton } from "@/components/kit";
import { useVariantImportPriceHistory } from "@/modules/inventory/presentation/api/import-orders-queries";
import {
  formatLakAmount,
  IMPORT_ORDER_SOURCE_LABELS,
  importOrderPriceUnitLabel,
} from "@/modules/inventory/presentation/lib/import-order-labels";
import { formatDateTimeLocal } from "@/shared/lib/date-time";

type VariantImportPriceHistoryPanelProps = {
  variantId: string;
  embedded?: boolean;
};

export function VariantImportPriceHistoryPanel({
  variantId,
  embedded,
}: VariantImportPriceHistoryPanelProps) {
  const nav = useNavigate();
  const history = useVariantImportPriceHistory(variantId);
  const rows = history.data?.data ?? [];

  if (history.isLoading) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-16 rounded-lg" />
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="text-muted-foreground text-xs">
        ຍັງບໍ່ມີປະຫວັດລາຄາຊື້ຈາກໃບສັ່ງນຳເຂົ້າ
      </p>
    );
  }

  return (
    <div className={embedded ? "flex flex-col gap-2" : "brand-card p-4"}>
      <h5 className="font-medium text-sm">ປະຫວັດລາຄາຊື້</h5>
      <ul className="flex flex-col gap-2">
        {rows.slice(0, 5).map((row) => (
          <li
            key={`${row.importOrderId}-${row.orderedAt ?? "na"}`}
            className="flex flex-col gap-1 rounded-lg border border-secondary/15 bg-background/60 px-3 py-2 text-xs sm:flex-row sm:items-center sm:justify-between"
          >
            <div className="min-w-0">
              <button
                type="button"
                className="font-medium text-secondary hover:underline"
                onClick={() =>
                  nav({
                    to: "/app/import-orders/$id",
                    params: { id: row.importOrderId },
                  })
                }
              >
                {row.externalOrderNo}
              </button>
              <p className="text-muted-foreground">
                {IMPORT_ORDER_SOURCE_LABELS[row.source]} ·{" "}
                {row.orderedAt
                  ? formatDateTimeLocal(row.orderedAt)
                  : "—"}{" "}
                · {row.orderedQuantity} ຊິ້ນ
              </p>
            </div>
            <div className="shrink-0 text-right tabular-nums">
              <p className="font-medium">
                {row.unitPriceForeign}{" "}
                {importOrderPriceUnitLabel(row.currency ?? "CNY")}
                {(row.currency ?? "CNY") === "LAK"
                  ? ""
                  : ` @ ${row.exchangeRate}`}
              </p>
              <p className="text-muted-foreground">
                landed {formatLakAmount(row.unitLandedCostLak)}/ຊິ້ນ
              </p>
            </div>
          </li>
        ))}
      </ul>
      {rows.length > 5 ? (
        <p className="text-muted-foreground text-xs">
          +{rows.length - 5} ຄັ້ງເພີ່ມເຕີມ
        </p>
      ) : null}
    </div>
  );
}
